import { useEffect, useState } from 'react';

import { clearCache, layout, prepare, type PreparedText } from '@chenglou/pretext';

import type { TranscriptRow } from './transcript-row-utils';
import type { AudioFileViewerSize } from '../audio-file-viewer';

// I'm keeping all these AI generated comments because they are probably useful when an agent
// comes to edit this feature again.

// Row heights are computed arithmetically, not measured per row, so the virtualizer stays static:
// exact total size on first paint and a scrollToIndex that lands first try.
//
// Column widths are read off one mounted row instead of derived from CSS — the CSS chain (scroller
// padding, row margins, grid tracks/gaps, span inset) is easy to get a few pixels wrong, which
// costs a whole line on rows that wrap near the column edge.
//
// Line heights below still have to track the CSS; the dev-only height check in `audio-transcript`
// catches it if they drift.

// `text-sm` + `--font-sans` from `index.css`
const transcriptFont = (size: AudioFileViewerSize) => `${size === 'sm' ? 12 : 14}px "Inter Variable", sans-serif`;

// Same font without the generic fallback, for `FontFaceSet.check`/`load` — with `sans-serif` in the
// string both resolve immediately.
const transcriptWebfont = (size: AudioFileViewerSize) => `${size === 'sm' ? 12 : 14}px "Inter Variable"`;

// Enough of the Latin subset to force the face this transcript renders in to download.
const FONT_LOAD_SAMPLE = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789,.';

// Both variants use a 28px line box. The compact variant reduces glyph size without changing the
// virtualized row cadence, which keeps scrolling stable. A wrapped speaker name can be the tallest
// grid item, so it is included in the model.
export const ROW_LINE_HEIGHT_PX = 28;

// `py-1` on the row button
const ROW_PADDING_PX = 8;

// `pt-3` on the first row of a speaker run, which replaced `space-y-10`
const SPEAKER_GAP_PX = 12;

// Inter's average lowercase advance is roughly half an em. This is only used before the webfont
// is ready; exact proportional glyph widths come from pretext after it loads.
const averageCharWidth = (size: AudioFileViewerSize) => (size === 'sm' ? 12 : 14) * 0.5;

// Content-box widths the two wrappable columns lay out in.
export interface TranscriptColumnWidths {
  text: number;
  speaker: number;
}

// Stand-in for the frames before a row has mounted: the scroller's `pl-10 pr-12`, the row's
// `-mx-3`/`px-2.5`, the `6.75rem` and `3.25rem` tracks with two `gap-4`s, and the span's
// `-mx-1.5 px-2`. Only ever paired with `estimateRowHeight` — `clientWidth` is whole-pixel, so it's
// up to a pixel off, which would move a line break in the exact model.
export const getFallbackColumnWidths = (scrollerClientWidth: number): TranscriptColumnWidths => ({
  text: Math.max(1, scrollerClientWidth - 280),
  speaker: 108,
});

// `px-2` on the text span. The speaker span has no inset.
const TEXT_SPAN_PADDING_PX = 16;

// Real widths off whichever row is mounted — null only on the first frame. Every row shares the
// same grid template, so any row answers for all.
export const readColumnWidths = (scroller: HTMLElement): TranscriptColumnWidths | null => {
  const textSpan = scroller.querySelector<HTMLElement>('[data-transcript-text]');
  const speakerSpan = scroller.querySelector<HTMLElement>('[data-transcript-speaker]');
  if (!textSpan || !speakerSpan) return null;

  // `getBoundingClientRect` includes ancestor transforms. Popovers open with a 0.98 scale animation,
  // which made these columns appear narrower than their layout widths and overestimated wrapped-row
  // heights. Computed widths describe the untransformed CSS layout used for line wrapping.
  const textWidth = Number.parseFloat(getComputedStyle(textSpan).width);
  const speakerWidth = Number.parseFloat(getComputedStyle(speakerSpan).width);
  if (!Number.isFinite(textWidth) || !Number.isFinite(speakerWidth)) return null;

  return {
    text: Math.max(1, textWidth - TEXT_SPAN_PADDING_PX),
    speaker: speakerWidth,
  };
};

// Both columns that can wrap, so a row's height is the taller of the two.
export interface PreparedRow {
  text: PreparedText;
  speakerLabel: PreparedText | null;
}

// Prepared handles positionally aligned to `rows`. `prepare` is the expensive step; `layout`
// against it is ~0.001ms, so a resize re-lays out every row for almost nothing.
//
// Deduped per call rather than in a module-level cache, which would retain a handle per block of
// every transcript the session opened.
export const prepareRows = (rows: TranscriptRow[], size: AudioFileViewerSize = 'default'): PreparedRow[] => {
  // A handle depends only on text and font, not width, so speaker labels share this cache.
  const preparedByText = new Map<string, PreparedText>();

  const preparedFor = (text: string) => {
    const cached = preparedByText.get(text);
    if (cached) return cached;

    const handle = prepare(text, transcriptFont(size));
    preparedByText.set(text, handle);
    return handle;
  };

  return rows.map((row) => ({
    text: preparedFor(row.text),
    speakerLabel: row.speakerLabel === null ? null : preparedFor(row.speakerLabel),
  }));
};

const rowChrome = (row: TranscriptRow) => ROW_PADDING_PX + (row.showsSpeakerGap ? SPEAKER_GAP_PX : 0);

export const measureRowHeight = (
  row: TranscriptRow,
  prepared: PreparedRow,
  widths: TranscriptColumnWidths,
  size: AudioFileViewerSize = 'default',
) => {
  const lineHeight = ROW_LINE_HEIGHT_PX;
  const textHeight = layout(prepared.text, widths.text, lineHeight).height + (row.hasLowConfidence ? lineHeight : 0);
  const speakerHeight = prepared.speakerLabel ? layout(prepared.speakerLabel, widths.speaker, lineHeight).height : 0;

  // The default timestamp has 1px top padding. The compact timestamp's optical transform does not
  // affect its layout height.
  const timestampHeight = lineHeight + (size === 'sm' ? 0 : 1);
  return Math.max(timestampHeight, textHeight, speakerHeight) + rowChrome(row);
};

// Character count against Inter's average advance width, ignoring break opportunities.
const estimateWrappedHeight = (text: string, columnWidth: number, size: AudioFileViewerSize) => {
  const charsPerLine = Math.max(1, Math.floor(columnWidth / averageCharWidth(size)));
  return Math.max(1, Math.ceil(text.length / charsPerLine)) * ROW_LINE_HEIGHT_PX;
};

// Used only until the webfont has loaded and the probe has been read, so first paint isn't blocked.
export const estimateRowHeight = (
  row: TranscriptRow,
  widths: TranscriptColumnWidths,
  size: AudioFileViewerSize = 'default',
) => {
  const lineHeight = ROW_LINE_HEIGHT_PX;
  const textHeight = estimateWrappedHeight(row.text, widths.text, size) + (row.hasLowConfidence ? lineHeight : 0);
  const speakerHeight = row.speakerLabel ? estimateWrappedHeight(row.speakerLabel, widths.speaker, size) : 0;

  const timestampHeight = lineHeight + (size === 'sm' ? 0 : 1);
  return Math.max(timestampHeight, textHeight, speakerHeight) + rowChrome(row);
};

// Whether canvas will measure the font the transcript actually renders in. Until it does, heights
// come from `estimateRowHeight` — a wrong-font measurement looks exact but isn't.
//
// Deliberately not `document.fonts.ready`, which resolves immediately when nothing is pending.
// `@fontsource`'s faces are `font-display: swap` and split by `unicode-range`, so Inter isn't
// fetched until something needs it: canvas would measure the generic fallback and cache it. The
// fallback can differ invisibly until a line ends near the column edge — then the row can be a
// whole 28px too tall or short.
export const useTranscriptFontReady = (size: AudioFileViewerSize = 'default') => {
  const webfont = transcriptWebfont(size);
  const [isReady, setIsReady] = useState(() => !document.fonts || document.fonts.check(webfont));

  useEffect(() => {
    if (isReady) return;

    let isCurrent = true;

    const adopt = () => {
      if (!isCurrent) return;
      // Pretext caches widths by (text, font string), which doesn't change when the webfont
      // arrives — so the fallback's measurements have to be dropped explicitly.
      clearCache();
      setIsReady(true);
    };

    // A failed load leaves the DOM on the fallback too, so canvas and layout still agree.
    void document.fonts.load(webfont, FONT_LOAD_SAMPLE).then(adopt, adopt);

    return () => {
      isCurrent = false;
    };
  }, [isReady, webfont]);

  return isReady;
};
